/**
 * Toast Notification System
 * 
 * Provides a simple, reusable toast notification system for success/error/info messages.
 */

import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';

// =============================================================================
// Types
// =============================================================================

export type ToastType = 'success' | 'error' | 'warning' | 'info';

/** Optional call-to-action rendered as a button inside the toast. */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
  action?: ToastAction;
}

interface ToastContextValue {
  toasts: Toast[];
  showToast: (type: ToastType, message: string, duration?: number, action?: ToastAction) => void;
  dismissToast: (id: string) => void;
}

// =============================================================================
// Context
// =============================================================================

const ToastContext = createContext<ToastContextValue | null>(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// =============================================================================
// Styles
// =============================================================================

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    pointerEvents: 'none',
  },
  toast: {
    padding: '16px 20px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    color: 'white',
    fontSize: '14px',
    fontWeight: '500',
    boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
    animation: 'slideInRight 0.3s ease-out',
    minWidth: '300px',
    maxWidth: '450px',
    pointerEvents: 'auto',
  },
  success: {
    background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
  },
  error: {
    background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
  },
  warning: {
    background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)',
  },
  info: {
    background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
  },
  icon: {
    fontSize: '20px',
    flexShrink: 0,
  },
  message: {
    flex: 1,
    lineHeight: '1.4',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: 'white',
    opacity: 0.7,
    cursor: 'pointer',
    padding: '4px',
    fontSize: '18px',
    lineHeight: 1,
    flexShrink: 0,
  },
  actionButton: {
    background: 'rgba(255,255,255,0.18)',
    border: '1px solid rgba(255,255,255,0.45)',
    borderRadius: '8px',
    color: 'white',
    cursor: 'pointer',
    padding: '6px 12px',
    fontSize: '13px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
};

const ICONS: Record<ToastType, string> = {
  success: '✓',
  error: '✕',
  warning: '⚠',
  info: 'ℹ',
};

// =============================================================================
// Toast Component
// =============================================================================

interface ToastItemProps {
  toast: Toast;
  onDismiss: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onDismiss }) => {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(() => {
        setIsExiting(true);
        setTimeout(() => onDismiss(toast.id), 300);
      }, toast.duration);

      return () => clearTimeout(timer);
    }
  }, [toast.id, toast.duration, onDismiss]);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => onDismiss(toast.id), 300);
  };

  return (
    <div
      style={{
        ...styles.toast,
        ...styles[toast.type],
        opacity: isExiting ? 0 : 1,
        transform: isExiting ? 'translateX(20px)' : 'translateX(0)',
        transition: 'opacity 0.3s ease, transform 0.3s ease',
      }}
      role="alert"
    >
      <span style={styles.icon}>{ICONS[toast.type]}</span>
      <span style={styles.message}>{toast.message}</span>
      {toast.action && (
        <button
          style={styles.actionButton}
          onClick={() => {
            toast.action?.onClick();
            handleDismiss();
          }}
        >
          {toast.action.label}
        </button>
      )}
      <button
        style={styles.closeButton}
        onClick={handleDismiss}
        aria-label="Dismiss notification"
      >
        ×
      </button>
    </div>
  );
};

// =============================================================================
// Provider Component
// =============================================================================

interface ToastProviderProps {
  children: React.ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showToast = useCallback((type: ToastType, message: string, duration = 5000, action?: ToastAction) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newToast: Toast = { id, type, message, duration, action };
    
    setToasts(prev => [...prev, newToast]);

    // Auto dismiss
    if (duration > 0) {
      setTimeout(() => dismissToast(id), duration + 300);
    }
  }, [dismissToast]);

  return (
    <ToastContext.Provider value={{ toasts, showToast, dismissToast }}>
      {children}
      <div style={styles.container}>
        {toasts.map(toast => (
          <ToastItem key={toast.id} toast={toast} onDismiss={dismissToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

// =============================================================================
// Convenience Hooks
// =============================================================================

/**
 * Hook for showing toast notifications
 * 
 * Usage:
 * ```tsx
 * const { success, error, warning, info } = useToastActions();
 * 
 * success('Backtest started successfully!');
 * error('Failed to submit backtest');
 * warning('API rate limit approaching');
 * info('Processing your request...');
 * ```
 */
export const useToastActions = () => {
  // Don't throw if there's no provider — return no-op handlers so optional
  // toast surfaces mounted inside isolated tests don't crash the host. The
  // real ToastProvider is mounted in `src/index.tsx` for the running app.
  const ctx = useContext(ToastContext);
  const showToast = ctx?.showToast;

  return {
    success: (message: string, duration?: number, action?: ToastAction) =>
      showToast?.('success', message, duration, action),
    error: (message: string, duration?: number) => showToast?.('error', message, duration ?? 8000),
    warning: (message: string, duration?: number) => showToast?.('warning', message, duration),
    info: (message: string, duration?: number) => showToast?.('info', message, duration),
  };
};

export default ToastProvider;
